import { Directive, HostListener, OnInit, OnDestroy, NgZone } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { filter, interval, map, Observable, Subject, Subscription, takeUntil } from 'rxjs';

import { Store } from '@ngrx/store';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import * as fromTicketState from '../../../+state/tickets';
import * as fromMasterdataState from '../../../+state/account/masterdata';
import * as fromPermissionState from '../../../+state/account/permissions';
import {
  IContract,
  IHQTicketDetailsResponse,
  IMetadataTicketDetailsResponse,
  ImmomioIconName,
  ITicketConversationEdit,
  ITicketMessage,
  ITicketMessageGroup,
  TicketingVersion,
} from '../../../models';
import { RouterService } from '../../../services';
import { IActionState } from '../../../utils';

@UntilDestroy()
@Directive()
export abstract class TicketChatPageBaseComponent implements OnInit, OnDestroy {
  public ticketDetails: IHQTicketDetailsResponse | IMetadataTicketDetailsResponse;
  public ticketChat$: Observable<ITicketMessageGroup[]>;
  public ticketChatActionState$: Observable<IActionState>;
  public newMessageActionState$: Observable<IActionState>;
  public selectedContract$: Observable<IContract>;
  public immomioIconName = ImmomioIconName;
  public ticketingVersion$: Observable<TicketingVersion> = this.masterdataStore
    .select(fromPermissionState.getResidentAppSettings)
    .pipe(
      map(response => response?.ticketingVersion),
      filter(response => response !== null)
    );
  protected destroy$?: Subject<boolean>;
  private updateInterval = 30000; // 30 seconds
  private routeSub: Subscription;

  constructor(
    private route: ActivatedRoute,
    public routerService: RouterService,
    protected ticketStore: Store<fromTicketState.ITicketsState>,
    private masterdataStore: Store<fromMasterdataState.IMasterDataState>,
    public ngZone: NgZone
  ) {}

  @HostListener('loaded')
  pageOnInit() {
    if (!this.destroy$) {
      this.destroy$ = new Subject();
    }
    this.selectedContract$ = this.masterdataStore.select(fromMasterdataState.getSelectedContract);
    this.ticketChat$ = this.ticketStore.select(fromTicketState.getTicketChat);
    this.ticketChatActionState$ = this.ticketStore.select(fromTicketState.getTicketChatActionState);
    this.newMessageActionState$ = this.ticketStore.select(fromTicketState.getNewMessageActionState);
  }

  @HostListener('unloaded')
  pageDestroy() {
    if (this.destroy$) {
      this.destroy$.next(true);
      this.destroy$.complete();
    }
  }

  ngOnInit(): void {
    this.routeSub = this.route.params.subscribe(params => {
      const ticketId = params['id'];
      if (ticketId) {
        this.getTicketDetails();
        this.loadTicketChat(ticketId);
        this.updateChat();
      }
    });
  }

  public ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  public sendMessage(newMessage: string | ITicketMessage): void {
    // TODO: Has to be updated for retry
    const message: ITicketConversationEdit = {
      ticketId: this.route.snapshot.params.id,
      message: newMessage as string,
      attachments: [],
    };

    this.ticketStore.dispatch(fromTicketState.AddMessageToTicket({ message }));
  }

  public goBack(): void {
    void this.routerService.navigate(['content', 'concern']);
  }

  protected loadTicketChat(ticketId?: string): void {
    this.ticketStore.dispatch(fromTicketState.LoadTicketChat({ ticketId: ticketId }));
  }

  private getTicketDetails(): void {
    this.ticketingVersion$.pipe(untilDestroyed(this)).subscribe(version => {
      if (version === TicketingVersion.HIERARCHICAL) {
        this.getHQTicketDetails();
      } else if (version === TicketingVersion.METADATA) {
        this.getMetadataTicketDetails();
      }
    });
  }

  private getHQTicketDetails(): void {
    this.ticketStore
      .select(fromTicketState.getTicketDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response) {
          this.ticketDetails = response;
        }
      });
  }

  private getMetadataTicketDetails(): void {
    this.ticketStore
      .select(fromTicketState.getMetadataTicketDetails)
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response) {
          this.ticketDetails = response;
        }
      });
  }

  private updateChat(): void {
    interval(this.updateInterval)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadTicketChat(this.route.snapshot.params.id));
  }
}
